import { useEffect, useState } from 'react'
import { ArrowRight, Loader2, Undo2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Badge } from '../../lib/shadcn/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { toast } from '../../lib/shadcn/sonner'
import { useGetCustomerMerges, useUndoCustomerMerge } from '../../hooks/backend/customersPage'
import { formatDateTime } from '../../utils/format'
import { reportStoreLocatorSync, type StoreLocatorSyncResult } from './storeLocatorSync'
import { HelpText } from '../../components/HelpText'

interface MergeRow {
  id: number
  kept_name: string | null
  merged_name: string | null
  orders: number
  totes: number
  messages: number
  filled_fields: string[]
  zoho_deactivated: boolean
  merged_by: string | null
  merged_at: string
  undone_by: string | null
  undone_at: string | null
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUndone: () => void
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

export default function CustomerMergeHistoryDialog({ open, onOpenChange, onUndone }: Props) {
  const getMerges = useGetCustomerMerges()
  const undoMerge = useUndoCustomerMerge()
  const [confirmId, setConfirmId] = useState<number | null>(null)
  const [undoingId, setUndoingId] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    setConfirmId(null)
    void getMerges.trigger(undefined, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const rows = (getMerges.data as MergeRow[] | undefined) ?? []

  const handleUndo = async (row: MergeRow) => {
    setUndoingId(row.id)
    try {
      const result = (await undoMerge.trigger({ merge_id: row.id })) as {
        restored_name: string | null
        orders: number
        totes: number
        warnings: string[]
        store_locator_sync: StoreLocatorSyncResult | null
      }
      toast.success(
        `Restored ${result.restored_name ?? 'the customer'} (${plural(result.orders, 'order')}, ${plural(result.totes, 'tote')} moved back)`
      )
      for (const warning of result.warnings) toast.warning(warning)
      reportStoreLocatorSync(result.store_locator_sync)
      onUndone()
      void getMerges.trigger(undefined, { skipCache: true })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Undo failed')
    } finally {
      setUndoingId(null)
      setConfirmId(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={next => { if (!next && undoingId === null) onOpenChange(false) }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Merge History</DialogTitle>
          <DialogDescription><HelpText>
            Undo puts the merged customer back with the orders, totes and messages that were moved. Details filled
            in on the kept customer are cleared again unless you've edited them since.
          </HelpText></DialogDescription>
        </DialogHeader>

        {getMerges.loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No customers have been merged yet.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {rows.map(row => (
              <div key={row.id} className="rounded-md border p-3 flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-0.5">
                  <div className="flex items-center gap-1.5 font-medium text-foreground flex-wrap">
                    {row.merged_name} <ArrowRight className="w-4 h-4" /> {row.kept_name}
                    {row.undone_at ? <Badge variant="outline" className="text-xs">Undone</Badge> : null}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(row.merged_at)}
                    {row.merged_by ? ` by ${row.merged_by}` : ''} · {plural(row.orders, 'order')},{' '}
                    {plural(row.totes, 'tote')}, {plural(row.messages, 'message')}
                    {row.filled_fields.length > 0 ? `, ${plural(row.filled_fields.length, 'field')} filled in` : ''}
                    {row.zoho_deactivated ? ' · inactive in Zoho' : ''}
                  </div>
                  {row.undone_at ? (
                    <div className="text-xs text-muted-foreground">
                      Undone {formatDateTime(row.undone_at)}{row.undone_by ? ` by ${row.undone_by}` : ''}
                    </div>
                  ) : null}
                </div>
                {row.undone_at ? null : confirmId === row.id ? (
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setConfirmId(null)} disabled={undoingId !== null}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => void handleUndo(row)} disabled={undoingId !== null}>
                      {undoingId === row.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                      Undo merge
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={() => setConfirmId(row.id)}
                    disabled={undoingId !== null}
                  >
                    <Undo2 className="w-4 h-4 mr-1" />
                    Undo
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
